import { css } from "@emotion/css";
import { Badge, Chip, EmptyStateNoResults, Pagination, SearchInput, Tab, Tabs } from "@octopusdeploy/design-system-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { ChannelResource, EphemeralEnvironmentProjectStatus, GetEphemeralEnvironmentsForProjectBffResponse, ProjectEphemeralEnvironmentDetail, ProjectEphemeralEnvironmentStatusCounts, ProjectResource, Repository, } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useCallback } from "react";
import { NotFound } from "~/components/NotFound/NotFound";
import { PageContent } from "~/components/PageContent/PageContent";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import type { ProjectContextProps } from "../../context";
import { EphemeralEnvironmentStatusIcon } from "./EphemeralEnvironmentStateIcon";
import { ProjectEphemeralEnvironmentsOnboarding } from "./ProjectEphemeralEnvironmentsOnboarding";
import { ProjectEphemeralEnvironmentsTable } from "./ProjectEphemeralEnvironmentsTable";
import { useEphemeralEnvironmentsEnabledForProject } from "./useEphemeralEnvironmentsEnabled";
const validTabValues = ["all", "active", "pending", "failed", "deprovisioned"];
type TabValues = "all" | "active" | "pending" | "failed" | "deprovisioned";
type ProjectEphemeralEnvironmentsPageProps = {
    project: ProjectResource;
    queryParams: ProjectEphemeralEnvironmentsQueryParams;
    setQueryParams: QueryParamValuesSetter<ProjectEphemeralEnvironmentsQueryParams>;
    loaderData: ProjectEphemeralEnvironmentsPageData;
};
export interface ProjectEphemeralEnvironmentsQueryParams {
    page: number | undefined;
    pageSize: number | undefined;
    environmentName: string;
    status: TabValues;
}
type ProjectEphemeralEnvironmentsPageData = {
    environments: GetEphemeralEnvironmentsForProjectBffResponse;
    channels: ChannelResource[];
};
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
function getPagingValues(queryParams: ProjectEphemeralEnvironmentsQueryParams): {
    currentPage: number;
    pageSize: number;
} {
    const currentPage = queryParams.page ?? 1;
    const pageSize = queryParams.pageSize ?? defaultPageSize;
    return { currentPage, pageSize };
}
async function getEnvironmentsForProject(repository: Repository, project: ProjectResource, queryParams: ProjectEphemeralEnvironmentsQueryParams) {
    const { currentPage, pageSize } = getPagingValues(queryParams);
    return await repository.EphemeralEnvironments.getForProject(project, (currentPage - 1) * pageSize, pageSize, queryParams.environmentName, statusFromTabValue(queryParams.status));
}
export async function projectEphemeralEnvironmentsPageLoader(repository: Repository, queryParams: ProjectEphemeralEnvironmentsQueryParams, projectContext: Promise<ProjectContextProps>): Promise<ProjectEphemeralEnvironmentsPageData> {
    const { state: { model: project }, } = await projectContext;
    const channels = repository.Channels.allFromProject(project);
    const ephemeralEnvironments = getEnvironmentsForProject(repository, project, queryParams);
    return {
        environments: await ephemeralEnvironments,
        channels: await channels,
    };
}
export function ProjectEphemeralEnvironmentsPage({ project, queryParams, setQueryParams, loaderData: { channels, environments: initialEnvironmentsForProject } }: ProjectEphemeralEnvironmentsPageProps) {
    const ephemeralEnvironmentsEnabledForProject = useEphemeralEnvironmentsEnabledForProject(project);
    const [debouncedSearch, setDebouncedSearch] = useDebouncedExternalState(queryParams.environmentName, (value) => setQueryParams({ ...queryParams, environmentName: value, page: 1 }), 200);
    const { currentPage, pageSize } = getPagingValues(queryParams);
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const onTabChange = useCallback((tab: string) => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        setQueryParams({ ...queryParams, page: 1, status: tab as TabValues });
    }, [queryParams, setQueryParams]);
    const { result: environmentsForProject, refetch: reloadEnvironmentsForProject } = useQuery(async (repository) => {
        return await getEnvironmentsForProject(repository, project, queryParams);
    }, [project, queryParams], "Get ephemeral environments for project", { refetchIntervalInMs: 5000, initialResult: initialEnvironmentsForProject });
    const { execute: deprovisionEnvironment } = useMutation({
        name: "Deprovision ephemeral environment",
        action: async (repository: Repository, environment: ProjectEphemeralEnvironmentDetail) => await repository.EphemeralEnvironments.deprovision(project.SpaceId, environment.Id),
        afterComplete: async () => reloadEnvironmentsForProject(),
    });
    const { execute: retryProvisioning } = useMutation({
        name: "Retry provisioning of ephemeral environment",
        action: async (repository: Repository, environment: ProjectEphemeralEnvironmentDetail) => await repository.EphemeralEnvironments.retryProvisioning(project.SpaceId, project.Id, environment.Id),
        afterComplete: async () => reloadEnvironmentsForProject(),
    });
    const { execute: retryDeprovisioning } = useMutation({
        name: "Retry deprovisioning of ephemeral environment",
        action: async (repository: Repository, environment: ProjectEphemeralEnvironmentDetail) => await repository.EphemeralEnvironments.retryDeprovisioning(project.SpaceId, project.Id, environment.Id),
        afterComplete: async () => reloadEnvironmentsForProject(),
    });
    const { execute: markProvisioningSuccessful } = useMutation({
        name: "Mark provisioning as successful for ephemeral environment",
        action: async (repository: Repository, environment: ProjectEphemeralEnvironmentDetail) => await repository.EphemeralEnvironments.markProvisioningSuccessful(project.SpaceId, project.Id, environment.Id),
        afterComplete: async () => reloadEnvironmentsForProject(),
    });
    const { execute: markDeprovisioningSuccessful } = useMutation({
        name: "Mark deprovisioning as successful for ephemeral environment",
        action: async (repository: Repository, environment: ProjectEphemeralEnvironmentDetail) => await repository.EphemeralEnvironments.markDeprovisioningSuccessful(project.SpaceId, project.Id, environment.Id),
        afterComplete: async () => reloadEnvironmentsForProject(),
    });
    if (!ephemeralEnvironmentsEnabledForProject) {
        return <NotFound />;
    }
    if (!hasAnyEnvironments(environmentsForProject.StatusCounts)) {
        return (<PageContent header={ephemeralEnvironmentsHeader()}>
                <ProjectEphemeralEnvironmentsOnboarding project={project} channels={channels}/>
            </PageContent>);
    }
    return (<PageContent header={ephemeralEnvironmentsHeader()} filters={{ inputs: [<SearchInput autoFocus={true} value={debouncedSearch} onChange={setDebouncedSearch} accessibleName={"Search ephemeral environments by name"} placeholder={"Search Environments"}/>] }} pagination={{
            ui: (<Pagination label="Environments" totalResults={environmentsForProject.Environments.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={pageSize} currentPage={currentPage} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: "topAndBottom",
        }}>
            <>
                <EphemeralEnvironmentFilters statusCounts={environmentsForProject.StatusCounts} activeState={queryParams.status} onTabChange={onTabChange}/>
                {environmentsForProject && environmentsForProject.Environments.TotalResults === 0 ? (<EmptyStateNoResults />) : (<ProjectEphemeralEnvironmentsTable project={project} environments={environmentsForProject.Environments.Items} deprovisionEnvironment={deprovisionEnvironment} retryProvisioning={retryProvisioning} retryDeprovisioning={retryDeprovisioning} markProvisioningSuccessful={markProvisioningSuccessful} markDeprovisioningSuccessful={markDeprovisioningSuccessful}/>)}
            </>
        </PageContent>);
}
interface EphemeralEnvironmentFiltersProps {
    statusCounts: ProjectEphemeralEnvironmentStatusCounts;
    activeState: TabValues;
    onTabChange: (tab: TabValues) => void;
}
function EphemeralEnvironmentFilters({ statusCounts, activeState, onTabChange }: EphemeralEnvironmentFiltersProps) {
    const total = statusCounts.Provisioning + statusCounts.Provisioned + statusCounts.ProvisioningFailed + statusCounts.Deprovisioning + statusCounts.DeprovisioningFailed + statusCounts.Deprovisioned;
    function tabChanged(value: string) {
        if (isValidTabValue(value)) {
            onTabChange(value);
        }
    }
    return (<Tabs value={activeState} onChange={(e, value) => tabChanged(value)} scrollButtons="auto" variant="scrollable">
            <Tab value="all" label={<EphemeralEnvironmentFiltersTabLabel name="All" count={total}/>}/>
            <Tab value="active" label={<EphemeralEnvironmentFiltersTabLabel name="Active" status="Provisioned" count={statusCounts.Provisioned}/>}/>
            <Tab value="pending" label={<EphemeralEnvironmentFiltersTabLabel name="Pending" status="Provisioning" count={statusCounts.Provisioning + statusCounts.Deprovisioning}/>}/>
            <Tab value="failed" label={<EphemeralEnvironmentFiltersTabLabel name="Failed" status="ProvisioningFailed" count={statusCounts.ProvisioningFailed + statusCounts.DeprovisioningFailed}/>}/>
            <Tab value="deprovisioned" label={<EphemeralEnvironmentFiltersTabLabel name="Deprovisioned" status="Deprovisioned" count={statusCounts.Deprovisioned}/>}/>
        </Tabs>);
}
interface EphemeralEnvironmentFiltersTabProps {
    name: string;
    count: number;
    status?: EphemeralEnvironmentProjectStatus;
}
function EphemeralEnvironmentFiltersTabLabel({ name, count, status }: EphemeralEnvironmentFiltersTabProps) {
    const styles = {
        container: css({
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: space[8],
        }),
    };
    return (<div className={styles.container}>
            {status && <EphemeralEnvironmentStatusIcon status={status} iconSize={16}/>} {name} <Badge variant="neutral" size="small" label={`${count}`}/>
        </div>);
}
function isValidTabValue(value: string): value is TabValues {
    return typeof value === "string" && validTabValues.includes(value);
}
function ephemeralEnvironmentsHeader() {
    return {
        title: `Ephemeral Environments`,
        chip: <Chip label="Alpha" backgroundColor={themeTokens.color.chip.filled.background.info} labelColor={themeTokens.color.chip.filled.text.info}/>,
    };
}
function hasAnyEnvironments(statusCounts: ProjectEphemeralEnvironmentStatusCounts) {
    return statusCounts.Provisioning > 0 || statusCounts.Provisioned > 0 || statusCounts.ProvisioningFailed > 0 || statusCounts.Deprovisioning > 0 || statusCounts.Deprovisioned > 0 || statusCounts.DeprovisioningFailed > 0;
}
function statusFromTabValue(tabValue: TabValues): EphemeralEnvironmentProjectStatus[] {
    switch (tabValue) {
        case "active":
            return ["Provisioned"];
        case "pending":
            return ["Provisioning", "Deprovisioning"];
        case "failed":
            return ["ProvisioningFailed", "DeprovisioningFailed"];
        case "deprovisioned":
            return ["Deprovisioned"];
        default:
            return [];
    }
}
